import type { ApiUserAccount, ApiSessionStarted, ApiOtpConfirmPayload } from 'src/types/userAuth';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { isTokenRejected } from 'src/utils/requestError';
import { markSignedIn, sessionEpoch } from 'src/utils/userSession';

import { QueryKeys } from './QueryKeys';

/** The header that asks the api for the session as a cookie rather than as tokens in the body (api#454). */
export const SESSION_TRANSPORT_HEADER = 'X-Session-Transport';

/**
 * The code was right and the api set the session — and the browser did not keep it: the very next
 * `/user/auth/me`, asked with the cookie the answer carried, was refused. Some settings and some
 * embedded webviews refuse cookies from another host than the page's; leaving the screen now would
 * look like a sign-in that did not take, so the screen says this instead.
 */
export class SessionNotKeptError extends Error {
  constructor() {
    super(
      'This browser did not keep the sign-in. Allow cookies for this site, or open GAC in Chrome or Safari.'
    );
    this.name = 'SessionNotKeptError';
  }
}

/**
 * The code was right and the api set the session — and the question whose it is got no answer: a
 * 502 while the api redeploys, a lost connection. The code is spent and the cookie may well be in
 * the browser, so the way on is a reload, not another code — and not the cookie message above,
 * which would send a person to their browser settings for an outage.
 */
export class SignInUnconfirmedError extends Error {
  constructor(public readonly cause: unknown) {
    super(
      'The code was accepted, but the sign-in could not be confirmed. Reload the page — you may already be signed in.'
    );
    this.name = 'SignInUnconfirmedError';
  }
}

export interface ConfirmedSignIn extends ApiSessionStarted {
  /** Who signed in, as `/user/auth/me` answered it with the new session. */
  account: ApiUserAccount;
}

/**
 * POST /user/auth/login/confirm — exchanges the OTP code + reference for the account session. Asked
 * for as a cookie (`X-Session-Transport: cookie`, api#454): the api sets `__Host-gac_session` on
 * the answer and the body says only when it expires; the storefront and the dealer cabinet hold
 * that one cookie together, so this sign-in is both sites'. The first confirm also verifies the
 * email.
 *
 * The mutation then asks `/user/auth/me` at once, with the cookie: a browser that did not keep it
 * is told so here (`SessionNotKeptError`), a question that got no answer as that
 * (`SignInUnconfirmedError`), and the account the api answers is put into the cache
 * under the new epoch, so the shell has it the moment the sign-in is announced — no splash, and no
 * second question.
 */
export const useConfirmUserLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ApiOtpConfirmPayload): Promise<ConfirmedSignIn> => {
      const started = await axios.post<ApiSessionStarted>(`/user/auth/login/confirm`, payload, {
        headers: { [SESSION_TRANSPORT_HEADER]: 'cookie' },
      });

      try {
        const me = await axios.get<ApiUserAccount>(`/user/auth/me`);

        return { ...started.data, account: me.data };
      } catch (error) {
        if (isTokenRejected(error)) throw new SessionNotKeptError();
        throw new SignInUnconfirmedError(error);
      }
    },
    onSuccess: ({ account }) => {
      markSignedIn();
      queryClient.setQueryData(QueryKeys.userMeFor(sessionEpoch()), account);
    },
  });
};
